/** Mirrors the API surface in `consulting_agent.api.app`. */

export type Depth = "scan" | "standard" | "deep";

export type UserRole = "member" | "admin";
export type UserStatus = "pending" | "active" | "suspended";

/** A user's uploaded documents. Named, never a path — see api/datarooms.py. */
/**
 * What a room is for. These are *source kinds*: the room's kind is what its
 * documents are ingested into the knowledge graph under, so a transcript is
 * stored as `interview` evidence and a client document as `data_room`.
 */
export type RoomKind = "data_room" | "interview";

export interface DataRoomSummary {
  id: string;
  name: string;
  kind: RoomKind;
  files: number;
  size_bytes: number;
  /**
   * Encrypted documents in this room that were never converted, by name.
   * Computed on read rather than remembered, so unlocking one clears it.
   * Named and not counted: one of them may not matter to this run.
   */
  locked: string[];
  /**
   * What each file was judged to be about, and by whom. A file with no entry
   * is **untagged**, which is not the same as tagged `Other`: one means
   * nobody has asked, the other means the question was put and the answer was
   * none of the list.
   */
  tags: Record<string, { industry: string; by: TagSource }>;
  /**
   * What the room as a whole is about, or `{}` while nobody has asked. The
   * topic is free text and the industry is from a closed list: one is read on
   * a panel and the other is counted in a breakdown.
   */
  topic: RoomTopic | Record<string, never>;
  /**
   * How many of *this reader's* engagements were given this room. From the
   * run rows rather than from a scan of every manifest on disk.
   */
  engagements: number;
  /**
   * For an interview room: what the call was — the expert, the network, the
   * date, the reuse terms. `{}` until somebody describes it.
   *
   * It becomes the `:Interview` node in the graph that every statement takes
   * its date and its terms from, and it is written from this form rather than
   * extracted: the rule that ingestion may not create entities exists to keep
   * an extraction model out, and a form is not extraction.
   */
  call: CallDetails | Record<string, never>;
}

/** What an interview room records about its call. Every field optional: a
 *  transcript with nothing but a date is still worth having. */
export interface CallDetails {
  /** The expert as they are known — often a descriptor, since most network
   *  experts are anonymised. */
  expert?: string;
  expert_basis?: string;
  /** The network's own id for them. The only identity an anonymised expert
   *  has, and what a second call resolves onto. */
  network_id?: string;
  anonymised?: boolean;
  seniority?: string;
  function?: string;
  affiliation?: string;
  country?: string;
  /** Where the speaker stood, which is the main bias signal in a call. */
  vantage?: string;
  date?: string;
  channel?: string;
  network?: string;
  /** The network's call id — the dedup key for a transcript bought twice. */
  network_ref?: string;
  duration_min?: number;
  language?: string;
  /** Whether a verbatim quote may be taken from it at all. */
  transcript_quality?: string;
  consent?: boolean;
  /** `engagement_only` scopes everything derived from the call to the client
   *  that commissioned it. Network terms often require it. */
  reuse?: string;
  retain_until?: string;
  call_rating?: number;
}

/** A label the agent proposed, or one a person confirmed. Provenance, the
 *  rule this codebase applies to every number. */
export type TagSource = "suggested" | "confirmed";

export interface RoomTopic {
  topic: string;
  industry: string;
  by: TagSource;
}

/** What `POST /api/data-rooms/{id}/tags` answered. `untagged` is what the
 *  model skipped or could not read — named, never a bare count. */
export interface TagResult extends DataRoomSummary {
  tagged: Record<string, string>;
  untagged: string[];
}

/**
 * One file on its way up, with the path it should keep.
 *
 * A `File` knows its basename and, for a folder picked through the file
 * dialog, `webkitRelativePath` — but a file dragged out of a folder knows
 * neither, because the path comes from walking the `DataTransfer` entries. So
 * the path travels beside the file rather than being read off it.
 */
export interface UploadItem {
  file: File;
  path: string;
}

/** What an upload did — on disk, and in the graph. */
export interface UploadResult extends DataRoomSummary {
  added: string[];
  /**
   * filename → why it was not stored. One refused file used to fail the whole
   * batch, which is survivable for a single upload and useless for a folder
   * of four hundred documents with one `.numbers` in it.
   */
  refused: Record<string, string>;
  /**
   * What the operating system put in the folder — `.DS_Store`, `__MACOSX`.
   * Separate from `refused` because it is not a failure: it is what a folder
   * drop always contains, and four of them must not read as four problems.
   */
  ignored: string[];
  indexed: {
    ingested: { title: string; passages: number; unchanged: boolean }[];
    /** filename → why it did not go in. Named, never a bare count. */
    skipped: Record<string, string>;
    /**
     * Files that are encrypted and were not opened. Separate from `skipped`
     * because this is the one failure the person can fix — a CIM arrives from
     * a bank with a password on it, and "holds no text" sends them looking at
     * the document instead of at the mail it came in.
     */
    locked: string[];
    note: string;
  };
}

/** One line of the audit trail. */
export interface AuditEvent {
  id: number;
  at: string;
  /** Dotted and stable — `auth.signed_in`, `run.finished`, `admin.role_changed`. */
  kind: string;
  actor_id: string | null;
  /** The actor's address as it was. Survives the account being removed. */
  actor: string | null;
  /** Who it was done *to*, for an administrative action. */
  subject: string | null;
  run_id: string | null;
  summary: string;
  detail: Record<string, unknown> | null;
}

/**
 * How deep to read the trail. Each level contains the ones above it.
 *
 * 1 `shape` — what happened: signed in, run started, finished at this cost, a
 * document uploaded, a role changed. 2 `detail` — how: each phase as it begins
 * and ends, credit moving, a tool server that did not connect. 3 `trace` —
 * every tool call the agent made, about a thousand a run, and only present
 * where the deployment writes them.
 */
export type AuditLevel = 1 | 2 | 3;

export const LEVEL_NAMES: Record<AuditLevel, string> = {
  1: "shape",
  2: "detail",
  3: "trace",
};

export interface AuditPage {
  events: AuditEvent[];
  /** Behind the page, so a narrow filter is distinguishable from an empty one. */
  total: number;
  /** The families actually present, read from the data rather than declared. */
  kinds: string[];
  level: AuditLevel;
  /**
   * The deepest level this deployment *writes*. Asking for trace where nothing
   * writes it returns an empty page, and without this the page would read as a
   * quiet system rather than as a setting.
   */
  writing: AuditLevel;
}

/** One statement put to the knowledge graph, and what came back. */
export interface MemoryQuery {
  id: number;
  at: string | null;
  mode: "read" | "write";
  operation: string | null;
  actor: string | null;
  run_id: string | null;
  cypher: string;
  params: Record<string, unknown> | null;
  duration_ms: number;
  /** The true count, even where the body below is an excerpt. */
  rows: number;
  result: unknown;
  error: string | null;
}

export interface MemoryQueryPage {
  queries: MemoryQuery[];
  total: number;
  writing: AuditLevel;
}

export interface CreditEntry {
  id: string;
  amount_usd: number;
  kind: "topup" | "charge" | "refund" | "grant" | "adjustment";
  run_id: string | null;
  note: string | null;
  created_at: string | null;
}

/** `GET /api/credits` — balance, statement, and what each depth reserves. */
export interface CreditState {
  balance_usd: number;
  held_usd: number;
  available_usd: number;
  history: CreditEntry[];
  /** What each depth reserves at the default model — a ceiling is priced by
   *  its model, so the New Engagement form asks `/api/ceilings` instead. */
  reserves_usd: Record<Depth, number>;
}

/**
 * `GET /api/ceilings` — every depth's ceiling for one playbook on one choice
 * of models. The form re-asks on each provider, model and subagent model it is
 * shown, so the figures beside the picker are the ones the run reserves.
 */
export interface Ceilings {
  provider: string;
  model: string;
  subagent_model: string | null;
  /** The same work's price on these models against `reference` (1.0). Null
   *  when a model has no declared price: the ceilings are then the
   *  reference figures, unscaled. */
  cost_factor: number | null;
  reference: { provider: string; model: string; label: string };
  ceiling_usd: Record<Depth, number>;
  reserves_usd: Record<Depth, number>;
}

/** One report offered by email (`GET /api/mail/proposals/{id}`). */
export interface ProposalOption {
  number: number;
  title: string;
  why: string;
  playbook: string;
  /** The playbook's title. */
  report: string;
  depth: Depth;
  /** What the request was read as — target, region, sector — on one line. */
  scope: string;
  inputs: Record<string, string>;
  /** The client's own questions, which this report answers along the way. */
  questions?: string[];
  /** What starting it would hold; null when it can no longer be priced. */
  hold_usd: number | null;
}

/** A proposal sent in answer to an emailed request. */
export interface Proposal {
  id: string;
  request: string;
  subject: string;
  status: "pending" | "open" | "ordered" | "unmatched" | "failed" | "expired";
  note: string | null;
  chosen: number | null;
  run_id: string | null;
  created_at: string | null;
  expires_at: string | null;
  balance_usd: number;
  options: ProposalOption[];
}

/** One message into or out of the mail interface (`GET /api/admin/mail`). */
export interface MailMessage {
  id: string;
  created_at: string;
  direction: "in" | "out";
  route: string;
  kind: string;
  proposal_id: string | null;
  user_id: string | null;
  sender: string;
  recipient: string;
  subject: string;
  message_id: string | null;
  verified: boolean | null;
  /** In: received · handled · dropped. Out: sent · failed · unsent. */
  outcome: string;
  note: string | null;
  /** A preview in the list, the whole text in a thread; null once deleted. */
  body: string | null;
  chars: number;
  /** The text was deleted after the retention period. */
  deleted: boolean;
}

export interface MailPage {
  messages: MailMessage[];
  next: string | null;
  keep_days: number;
  thread: {
    id: string;
    status: string;
    chosen: number | null;
    run_id: string | null;
    parent_id: string | null;
    owner: string | null;
    options: string[];
    /** What the model call that made the proposal cost. */
    cost_usd: number;
  } | null;
  /** Proposals made in the last `days`, and what their model calls cost. */
  spend: { days: number; proposals: number; cost_usd: number };
}

export interface CreditPackage {
  id: string;
  label: string;
  /** In `currency` — what the customer pays. */
  price: number;
  currency: string;
  /** What lands in the ledger. Stated separately: no FX rate in the code. */
  credit_usd: number;
}

export interface BillingCatalogue {
  /** False when Stripe is not fully configured; the page says so. */
  configured: boolean;
  currency: string;
  packages: CreditPackage[];
}

/** `GET /api/admin/users` — every user, with what they hold. */
/**
 * One evaluation: a run, the document it was graded against, and the headline
 * once the comparison has finished.
 *
 * `headline` and `has_report` are absent while the run is still going, which
 * is the common case for anyone who opens the page just after starting one —
 * so the row has to render without them rather than treating them as missing
 * data.
 */
export interface AdminEvaluation {
  run_id: string;
  playbook: string;
  reference: string;
  status: string;
  target: string | null;
  started_at: string | null;
  cost_usd: number | null;
  headline: string | null;
  has_report: boolean;
  /**
   * A content fingerprint of the playbook's declaration and every prompt,
   * taken when the run started. Null for an evaluation whose row failed to
   * write — shown as unknown, never as the current version, because "which
   * prompts produced this" is the question the whole page is for.
   */
  playbook_version: string | null;
  verdicts: Record<string, string>;
  /** What was changed in the playbook because of this. Editable. */
  improvement: string | null;
  /**
   * True when a run that had already finished was graded afterwards, rather
   * than bought to be graded. It is what makes the blank version above
   * readable: that run was made before anybody thought of grading it, so its
   * playbook fingerprint is unknown rather than missing.
   */
  existing_run?: boolean;
}

export interface AdminEvaluationReport {
  run_id: string;
  playbook: string;
  reference: string;
  /** Null until the run has finished and the comparison has written it. */
  report: string | null;
  playbook_version: string | null;
  verdicts: Record<string, string>;
  improvement: string | null;
  /**
   * Every earlier evaluation of this playbook against this same document.
   * The loop is this list: the same question asked of two versions, with
   * what changed between them written down.
   */
  history: AdminEvaluation[];
}

/** A reference room and what is in it — `GET /api/admin/evaluations/references`. */
export interface ReferenceRoom extends DataRoomSummary {
  file_names: string[];
}

export interface AdminUser extends CurrentUser {
  created_at: string | null;
  last_seen_at: string | null;
  /** Where its notices go when not to the sign-in address (`cdd notify-address`). */
  notify_email: string | null;
}

/** `GET /api/auth/session` — public, so the shell knows what to render. */
export interface SessionState {
  authenticated: boolean;
  status: UserStatus | null;
  /** Whose session this is, at any status — the waiting page names it. */
  email: string | null;
}

/** Where an emailed sign-in link stands. Checking it spends nothing. */
export type LinkState = "ready" | "used" | "expired" | "invalid";

/** `POST /api/auth/email/peek`. */
export interface LinkCheck {
  state: LinkState;
  /** The address the link was sent to; null only for a link that never existed. */
  email: string | null;
  /** This browser already holds the session the link would give. */
  signed_in: boolean;
}

/** A link that will not sign anybody in. The page branches on `code`. */
export interface LinkRefusal {
  code: Exclude<LinkState, "ready"> | "refused";
  detail: string;
  email: string | null;
}

/** `POST /api/auth/email/redeem` when it worked: where to go next. */
export interface LinkRedeemed {
  next: string;
  status: UserStatus;
  email: string;
}

export interface AuthProviders {
  providers: { key: string; label: string }[];
  /** False when the deployment has no sign-in configured at all. */
  configured: boolean;
  /** Whether an emailed one-time link is offered beside the providers. */
  email_login: boolean;
}

/**
 * The beta terms, served with the identity so the modal can decide whether to
 * appear and render itself in one round trip. The wording lives in
 * `core/accounts/disclaimer.py` — never inline here, or the version the user's
 * acceptance names would not identify any particular text.
 */
export interface Disclaimer {
  version: string;
  title: string;
  body: string;
  prohibited: string[];
  because: string[];
  confirm: string;
}

/** `GET /api/me` — the user, plus what they can spend. */
export interface CurrentUser {
  id: string;
  email: string;
  name: string | null;
  avatar_url: string | null;
  role: UserRole;
  status: UserStatus;
  balance_usd: number;
  held_usd: number;
  available_usd: number;
  /** Null until they accept. Compared against `disclaimer.version`, not
   *  tested for presence — new wording is a new agreement. */
  disclaimer_version: string | null;
  disclaimer_accepted_at: string | null;
  disclaimer: Disclaimer;
}

export type ProjectStatus = "planned" | "ongoing" | "completed" | "failed";

/** 1 is excellent, 6 is bad. Only completed projects may carry one. */
export type Score = 1 | 2 | 3 | 4 | 5 | 6;

export interface Project {
  id: number;
  name: string;
  description: string | null;
  client: string | null;
  project_type: string;
  status: ProjectStatus;
  start_date: string | null;
  end_date: string | null;
  score: Score | null;
  score_label: string | null;
  score_note: string | null;
  scored_at: string | null;
  scored_by: string | null;
  run_id: string | null;
  playbook: string | null;
  target: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface ProjectSummary {
  total: number;
  by_status: Record<ProjectStatus, number>;
  by_type: Record<string, number>;
  scored: number;
  average_score: number | null;
  average_score_by_type: Record<string, number>;
}
export type Confidence = "high" | "medium" | "low";
export type PhaseStatus =
  | "pending"
  | "running"
  | "done"
  | "failed"
  // A run that ended while this phase was running. Written by `complete()`
  // reconciling its own phase records, not by the phase itself — which is why
  // it is distinct from `failed`: a phase interrupted by a cancellation did
  // not fail, and a reader counting failures deserves the difference.
  | "cancelled"
  | "skipped";

export interface RunSummary {
  run_id: string;
  status: string;
  target: string | null;
  playbook: string | null;
  sector: string | null;
  depth: Depth | null;
  /** Absent on a run older than the provider choice (2026-08-03). */
  provider?: string | null;
  model?: string | null;
  subagent_model?: string | null;
  started_at: string | null;
  finished_at: string | null;
  current_phase: string | null;
  phases_done: number;
  phases_total: number;
  findings: number;
  cost_usd: number;
  /** 1-based place in the queue, when the run is waiting for a slot. */
  queue_position?: number | null;
}

export interface Usage {
  input_tokens: number;
  output_tokens: number;
  cache_read_tokens: number;
  cache_write_tokens: number;
  cost_usd: number;
}

export interface PhaseRecord {
  key: string;
  status: PhaseStatus;
  started_at: string | null;
  finished_at: string | null;
  duration_s: number;
  turns: number;
  usage: Partial<Usage>;
  error: string | null;
}

export interface EngagementInput {
  target: string;
  playbook: string;
  sector: string | null;
  geography: string;
  thesis: string | null;
  /** A free-text instruction, in place of a playbook's mandate. */
  prompt: string | null;
  client: string | null;
  questions: string[];
  depth: Depth;
  provider: string;
  model: string;
  subagent_model: string | null;
  data_room_id: string | null;
  max_revisions: number;
  /** Consult and contribute to the cross-engagement graph. */
  use_memory?: boolean;
  /** Wall this engagement off from other clients' work. */
  isolate_memory?: boolean;
  max_cost_usd?: number | null;
  project_id?: number | null;
  project_name?: string | null;
  project_type?: string | null;
}

export interface MemoryCounts {
  entities: number;
  relations: number;
  outcomes: number;
  mentions: number;
}

export interface Manifest {
  run_id: string;
  status: string;
  /**
   * Why the run ended this way. Present on a failed or cancelled run; the
   * manifest used to carry only the status, which sent anybody asking "why"
   * to `events.jsonl`.
   */
  error?: string | null;
  /** What the orchestrator wants a person to know — a salvaged deliverable, a
   * spend ceiling reached, a subsystem that was unavailable. */
  notes?: string[];
  /** About the system that produced the run, not the run — a packaging
   * fault, a register outage. The API sends these to admins only and an
   * empty list to everybody else (#124). */
  operator_notes?: string[];
  /** True when this run continued an earlier, interrupted segment. */
  resumed?: boolean;
  started_at: string;
  finished_at: string | null;
  engagement: EngagementInput;
  prompt_versions: Record<string, string>;
  phases: PhaseRecord[];
  usage: Usage;
  evidence: {
    findings: number;
    coverage: Record<string, number>;
    confidence_mix: Record<Confidence, number>;
    sources_observed: number;
    unsourced_findings: number[];
    unverified_findings: number[];
  };
  /**
   * What the graph held when the run connected, and what it held at the end.
   * `contributed` is the difference — the run's own answer to "did memorise
   * actually persist anything?". `after` and `contributed` are null until the
   * run finishes, since the reading is taken once, at the end.
   */
  memory: {
    available: boolean;
    backend?: string | null;
    uri?: string | null;
    before?: MemoryCounts;
    after?: MemoryCounts | null;
    contributed?: MemoryCounts | null;
    error?: string;
  };
  deliverables: string[];
  /**
   * Which of those files are the point — the playbook's `primary_deliverable`
   * and anything in `additional_deliverables`. Absent on runs finished before
   * the manifest carried it, which is why the client falls back rather than
   * assuming.
   */
  declared_deliverables?: string[];
  /**
   * Work done on this run *after* it finished. A field of its own rather than
   * a `status` of "running", because a finished engagement being worked on is
   * not an engagement in flight — the queue must not count it and Cancel does
   * not apply to it.
   */
  task?: RunTask | null;
  /**
   * The phase keys this run set out to walk, in order. Recorded at run start,
   * so the timeline shows what is still ahead without asking the playbook
   * registry — which has no answer for a deleted playbook and a stale one for
   * a playbook whose phases changed after the run. Absent on runs finished
   * before the manifest carried it.
   */
  planned_phases?: string[];
  live?: boolean;
  queue_position?: number | null;
  job_error?: string | null;
  project?: Project | null;
}

export interface Source {
  title: string;
  url: string | null;
  published: string | null;
  kind: string;
  verified: boolean;
}

export interface Finding {
  id: number;
  category: string;
  claim: string;
  so_what: string;
  confidence: Confidence;
  sources: Source[];
  quantification: string | null;
  caveats: string | null;
  recorded_by: string;
  recorded_at: string;
}

export interface ObservedSource {
  url: string;
  canonical: string;
  via: string;
  title: string | null;
  observed_at: string;
}

export interface Artefact {
  name: string;
  size_bytes: number;
  modified_at: number;
  /** From the server, so the client picks a viewer rather than guessing. */
  media_type: string;
}

/**
 * A deck read back as text. `skills/deck` refuses a slide title of fewer than
 * four words because the titles read in sequence are the argument — so this is
 * the part of a .pptx worth showing without a rendering engine.
 */
export interface DeckSlide {
  number: number;
  title: string;
  body: string[];
}

export interface DeckOutline {
  name: string;
  slides: DeckSlide[];
}

export interface Workstream {
  key: string;
  title: string;
  objective: string;
  key_questions: string[];
  evidence_sought: string[];
}

/** The brief fields a playbook may re-describe. Mirrors `ENGAGEMENT_INPUTS`. */
export type BriefField =
  | "target"
  | "sector"
  | "geography"
  | "thesis"
  | "client"
  | "questions"
  | "prompt"
  // Declarable for wording and visibility only. Whether a room is *required*
  // is `requires_data_room` on the playbook, which the server refuses on
  // before a run starts — the loader rejects `required` here so the two
  // cannot disagree.
  | "data_room";

export interface InputSpec {
  label: string | null;
  hint: string | null;
  placeholder: string | null;
  required: boolean;
  hidden: boolean;
}

export interface Playbook {
  key: string;
  title: string;
  description: string;
  /** `private` keeps this playbook's findings to whoever ran it. */
  access: "public" | "private";
  /**
   * This playbook reviews a document you supply, so it cannot start without a
   * data room holding it. The server refuses the run either way — this is so
   * the form can ask for it rather than offering a run that would only 400.
   */
  requires_data_room: boolean;
  /**
   * How this playbook words the brief. Absent keys mean "as always", so a
   * playbook that declares nothing gets the form every playbook had — which is
   * what keeps this a description rather than a second schema.
   */
  inputs: Partial<Record<BriefField, InputSpec>>;
  primary_deliverable: string;
  /** The start page's group — a key of `Options.playbook_categories`; null
   * files the tile under "Other". */
  category: string | null;
  /** The playbook's own spend ceiling per depth, where it declares one, in
   *  reference-model dollars. */
  cost_usd: Partial<Record<Depth, number>>;
  /** What a run of it reserves per depth at the default model — its own
   *  figure or the depth's, priced by that model. What a tile quotes. */
  ceiling_usd: Record<Depth, number>;
  /** Who it is for, in their own words. Three at most — it is a caption. */
  audience: string[];
  /** Figure types this playbook may draw. Empty means all of them. */
  figures: string[];
  /**
   * Whose it is. `null` for the ones this distribution ships, which is what
   * the Playbooks page divides on — and what an anonymous caller only ever
   * sees, because a title somebody wrote can carry a client's name.
   */
  owner_id: string | null;
  workstreams: Workstream[];
  phases: {
    key: string;
    title: string;
    gate: boolean;
    local: boolean;
    on_demand: boolean;
  }[];
}

export interface ToolServer {
  key: string;
  title: string;
  description: string;
  transport: string;
  available: boolean;
  missing: string[];
  install_hint: string;
  allowed_tools: string[] | null;
  /** Roles the catalog suggests it for. A hint, never a grant —
   *  the playbook decides via `tool_servers`. Named `suggested_for` on the
   *  wire; this said `subagents` and was therefore always undefined. */
  suggested_for: string[];
  bundled: boolean;
  requires_env: string[];
}

/** USD per million tokens, as the provider bills them. */
export interface Price {
  input: number;
  output: number;
  cache_read: number;
  cache_write: number;
  source: string;
}

export interface ProviderModel {
  id: string;
  label: string;
  note: string;
  /** List price, where the harness's figure is that provider's bill. */
  price: Price | null;
  /** What the provider bills as a share of the harness's figure, measured
   *  from a bill — Kimi K3's is 0.71. Null when nobody has measured one. */
  billed_share: number | null;
  /** Its price for the same work against the reference model (1.0), with
   *  subagents on the same model. Null when it has no price. */
  cost_factor: number | null;
}

/**
 * An endpoint the run can be sent to. The harness is the same for all of
 * them — this picks the model behind it, not the runtime.
 */
export interface Provider {
  key: string;
  label: string;
  models: ProviderModel[];
  default_model: string;
  /** False when the server has no key for it; the form disables the option. */
  configured: boolean;
  api_key_env: string | null;
  /** Capability keys this endpoint cannot serve, e.g. `web_fetch`. */
  unsupported: string[];
  note: string;
}

/** A paid action on a finished run, and what it may spend (#124). */
export interface PaidAction {
  ceiling_usd: number;
  charged_to_credit: boolean;
}

export interface Options {
  actions: {
    summary: PaidAction;
    deck: PaidAction;
    discussion: PaidAction;
    finetune: PaidAction;
    memory_summary: PaidAction;
  };
  /** The start page's groups, in the order it shows them. */
  playbook_categories: { key: string; label: string }[];
  depths: {
    key: Depth;
    research_turns: number;
    subagent_turns: number;
    default_cost_usd: number;
    guidance: string;
  }[];
  providers: Provider[];
  default_provider: string;
  /** The model every USD ceiling was calibrated on: cost factor 1.0. */
  cost_reference: { provider: string; model: string };
  models: string[];
  confidence: Record<Confidence, string>;
  project_types: string[];
  project_statuses: ProjectStatus[];
  scores: { value: Score; label: string }[];
}

/** One line off the SSE stream. Payload shape varies by `type`. */
export interface RunEvent {
  type: string;
  at?: string;
  elapsed_s?: number;
  phase?: string;
  tool?: string;
  summary?: string;
  preview?: string;
  status?: string;
  verdict?: string;
  error?: string | null;
  turns?: number;
  duration_s?: number;
  cost_usd?: number;
  is_error?: boolean;
  /** A bounded excerpt of what a tool answered — `tool.result` only. */
  text?: string;
  /** The answer's true length, so a short one reads differently from a cut one. */
  chars?: number;
  urls?: string[];
  via?: string;
  /**
   * Post-hoc work on a finished run — `task.start`, `task.step` and
   * `task.end`. The kind is what the button was ("deck", "summary"), the source is the deliverable it was
   * built from, and `decks` is what actually landed in the workspace.
   */
  kind?: string;
  source?: string;
  decks?: string[];
  storyline?: string | null;
  /** The file a summary landed in — `task.end` of kind "summary" only. */
  file?: string | null;
  /** The part a finetune is of, and what it changed — kind "finetune" only. */
  target?: string;
  changed?: string[];
  [key: string]: unknown;
}

/** One figure type, as the panel that offers them reads it. */
export interface FigureType {
  type: string;
  kind: "chart" | "diagram";
  /** The type's own one-line "reach for it when", from its `FIGURE.md`. */
  when: string;
  shape: string;
  also: string;
}

/** What the create form collects. Everything else is derived or the template's. */
export interface NewPlaybook {
  title: string;
  description: string;
  intent: string;
  audience: string[];
  figures: string[];
  workstreams: string[];
  /**
   * Let the model design the workstreams from `intent` instead of taking the
   * ones typed. Off by default: the template answers instantly on no budget,
   * and this is a model call on your credit.
   */
  draft_with_model?: boolean;
}

/** One retrieval result in the admin's memory console. */
export interface MemoryHit {
  kind: string;
  key: string;
  title: string;
  text: string;
  score: number;
  /** Why this row was admitted — what makes the panel an evaluation of the
   * retrieval rather than a list of names. */
  why: string;
  sector: string | null;
  run_id: string | null;
  age_days: number | null;
  confidence: string | null;
  sources: string[];
  /** "subject" — what the question names; "answer" — what it asked for of
   *  it (a competitor, a figure); null — found by its words, context. */
  role: "subject" | "answer" | null;
}

export interface MemoryAnswer {
  question: string;
  hits: MemoryHit[];
  /** Nothing matched — a fact about the graph, not a failure of the box. */
  empty: boolean;
}

/** `POST /api/admin/memory/cypher`. Translating never runs anything. */
/** The company network and corroboration, as the Memory page's Graph tab draws them. */
export interface MemoryGraph {
  meta: { method: "leiden" | "stored"; seed: number; min_comention: number; companies: number };
  /** Cluster names, the largest first; the last is "Other". */
  groups: string[];
  /** [name, group, findings] */
  nodes: [string, number, number][];
  /** [source, target, weight] — weight 0 is a recorded competitor, more is shared mentions. */
  links: [number, number, number][];
  /** [target, playbook, findings on 0, 1, 2, 3+ providers] */
  evidence: [string, string, number, number, number, number][];
}

/** One node of a company's neighbourhood, with what its pop-up says. */
export interface MemoryCompanyNode {
  name: string;
  /** The ontology label — Company, Market, Person, … */
  label: string;
  /** Shortened to a pop-up's length by the server. */
  description: string;
  /** [property, value] for the label's declared properties that are set. */
  facts: [string, string][];
  aliases: string[];
  sources: number;
  source_kinds: string[];
  /** YYYY-MM-DD, or "". */
  seen: string;
  /** The centre only: findings that name it, and its newest figures. */
  findings?: number;
  figures?: string[];
}

/** One recorded edge between the company and a neighbour. */
export interface MemoryCompanyRelation {
  type: string;
  /** Read the way the edge points: "Glacier supplies to Northwind". */
  sentence: string;
  description: string;
  since: string | null;
  until: string | null;
  recorded: string;
  /** How many times the ledger asserted it. */
  times: number;
}

/** `GET /api/memory/graph/company` — one company and what it is joined to. */
export interface MemoryCompany {
  query: string;
  /** The company drawn, or null when the name fits none or several. */
  company: string | null;
  /** Names what was typed fits, best first. */
  candidates: string[];
  /** The centre is node 0. */
  nodes: MemoryCompanyNode[];
  /** Centre to one neighbour: every relation between them, and how many
   *  findings name them together (0 when none). */
  links: { s: number; t: number; relations: MemoryCompanyRelation[]; findings: number }[];
}

/** One numbered item a memory briefing was written from (#135). */
export interface MemoryReference {
  /** "M3" — what the briefing cites. */
  id: string;
  kind: string;
  /** The node's key, or `subject|TYPE|neighbour` for an edge. */
  key: string;
  title: string;
  detail: string;
  run_id: string | null;
  sources: { title: string; url: string }[];
  cited: boolean;
}

export interface MemorySummary {
  question: string;
  /** The model's briefing, citing `[M3]`. */
  body: string;
  /** The body with the cited references appended — what a download saves. */
  markdown: string;
  references: MemoryReference[];
  /** Cited and never handed over — the sentences to distrust. */
  unknown: string[];
  material: number;
  cost_usd: number;
}

export interface MemoryCypherResult {
  cypher: string;
  ran: boolean;
  /** The guard's reason, when it declined. A result, not an error. */
  refused?: string | null;
  error?: string;
  rows?: Record<string, unknown>[];
  count?: number;
  truncated?: boolean;
  limit?: number;
  empty?: boolean;
  cost_usd?: number;
  limit_imposed?: boolean;
}

/** One exchange in a discussion about a finished run. */
export interface DiscussionTurn {
  role: "user" | "assistant";
  text: string;
  at: string;
  cost_usd: number;
  error?: string | null;
}

/**
 * The part of a deliverable a reader pointed at, as the page saw it.
 *
 * `line` is the markdown line the renderer stamped on the heading or figure,
 * and `label` the text the reader saw: the server finds the part by the first
 * and checks it by the second, and refuses a page that no longer matches.
 */
export interface FinetuneTarget {
  kind: "section" | "figure";
  line: number;
  label: string;
  /** "Figure 10", for a figure. */
  caption?: string;
  /** The heading's level; 0 for a figure. */
  level?: number;
}

/** One finetune of a run and what became of it (`core/finetune.Request`). */
export interface FinetuneRequest {
  id: string;
  /** The markdown deliverable it changed. */
  name: string;
  target: {
    kind: "section" | "figure";
    line: number;
    end: number;
    level: number;
    label: string;
    figure: string;
    src: string;
  };
  request: string;
  at: string;
  status: "running" | "done" | "unchanged" | "failed" | "interrupted";
  /** The agent's closing words: what changed. */
  said: string;
  error: string | null;
  cost_usd: number;
  /** What landed on disk, read off it rather than taken from the reply. */
  changed: string[];
  notes: string[];
  undone: boolean;
  /** Only the latest finetune of a document can be undone, once. */
  undoable: boolean;
}

export interface Finetunes {
  requests: FinetuneRequest[];
  running: boolean;
}

export interface Discussion {
  run_id: string;
  turns: DiscussionTurn[];
  /** The files put into the context, in order — so the page can say what the
   * agent was handed rather than implying it read everything. */
  loaded: string[];
  /** Named, not hidden: too large to inline, and readable on demand. */
  omitted: string[];
  /** The documents are in the context and will not be sent again. */
  context_loaded: boolean;
  cost_usd: number;
  /** The question being answered, saved when it was asked — so a reader who
   * left the page finds it on their return, and a second one waits. */
  pending: { text: string; at: string } | null;
}

/**
 * Work done on a run *after* it finished — a deck built from a deliverable,
 * or the board summary written from all of them. Its own field on the run rather than folded into `status`,
 * because a finished run being worked on is not a running engagement.
 */
export type RunTask = {
  kind: string;
  status: "running" | "completed" | "failed";
  started_at: number;
  finished_at: number | null;
  elapsed_s: number;
  error?: string | null;
  decks?: string[];
  said?: string;
  source?: string;
  /**
   * The checked storyline the deck was built with, or null when there was
   * none. Read off the workspace rather than off the model's reply: nothing
   * enforces the order, so this is the only honest answer to "was the
   * argument checked before the slides".
   */
  storyline?: string | null;
  /** The file a summary landed in (`summary.md`), or null if none was written. */
  file?: string | null;
  /** The summary was already on disk and cost nothing. */
  cached?: boolean;
};
